/** @jsxImportSource @emotion/react */
"use client";

import type { ArrayInterpolation, Interpolation } from "@emotion/react";
import { forwardRef, type PropsWithChildren } from "react";

import { useTransition } from "../../../../providers/TransitionRoot/TransitionRoot.emotion";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import { useTransitionStyles } from "./styles/useTransitionStyles.emotion";
import type { CSSAngle } from "./styles/wipe.emotion";

const baseStyle: Interpolation = {
  left: 0,
  position: "absolute",
  top: 0,
  transitionTimingFunction: "ease-in-out",
};
const speedStyle: Interpolation<{ duration: number }> = (props) => ({
  transitionDuration: `${props.duration}ms`,
});
const enteredBaseStyle: Interpolation = {
  position: "relative",
};
const modifySpeedStyle: Interpolation<{ delay: number }> = (props) => ({
  transitionDelay: `${props.delay}ms`,
});

const hallowElement = "div";

export type TransitionDirection =
  | "from-right"
  | "from-left"
  | "from-top"
  | "from-bottom";
export type TransitionShape = "square" | "circle" | "cross";
export type TransitionKind =
  | { kind: "push"; direction: TransitionDirection }
  | { kind: "cover"; direction: TransitionDirection }
  | { kind: "wipe"; angle: CSSAngle }
  | { kind: "fade" }
  | { kind: "shape"; shape: TransitionShape }
  | {
      kind: "custom";
      baseCSS?: Interpolation;
      enteringCSS?: Interpolation;
      enteredCSS?: Interpolation;
      exitingCSS?: Interpolation;
      exitedCSS?: Interpolation;
    };

export type TransitionProps = PropsWithChildren<
  WithAsChild<
    {
      value: string;
    } & HallowElementProps<"div">
  >
>;

export const Transition = forwardRef<HTMLDivElement, TransitionProps>(
  ({ children, value, asChild = false, ...elementProps }, ref) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });
    const {
      entering,
      entered,
      exiting,
      exited,
      transitionMs,
      transitionInDelay,
      transitionOutDelay,
      ...kindProps
    } = useTransition();

    const [baseCSS, enteringCSS, enteredCSS, exitingCSS, exitedCSS] =
      useTransitionStyles(kindProps);

    if (![entering, entered, exiting, exited].includes(value)) return null;

    const appliedStyles: ArrayInterpolation = [
      baseStyle,
      speedStyle({ duration: transitionMs }),
      baseCSS,
      entering === value
        ? [modifySpeedStyle({ delay: transitionInDelay }), enteringCSS]
        : null,
      entered === value ? [enteredBaseStyle, enteredCSS] : null,
      exiting === value
        ? [modifySpeedStyle({ delay: transitionOutDelay }), exitingCSS]
        : null,
      exited === value ? exitedCSS : null,
    ];
    const props = { ...elementProps, css: appliedStyles };

    return (
      <DeterminedElement ref={ref} {...props}>
        {children}
      </DeterminedElement>
    );
  },
);
